/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState, useRef, useCallback, ReactFragment, ReactChild} from 'react';
import classNames from 'classnames';
import Tooltip, {TooltipTrigger, TooltipProps} from '../Tooltip';
import styles from './index.module.scss';
import {throttle} from 'lodash-es';

interface EllipsisTooltipProps extends TooltipProps {
    children: ReactChild | ReactFragment;
    tailContent?: ReactChild | ReactFragment;
    tooltipContent?: ReactChild | ReactFragment;
    tooltipEnable?: boolean;
    className?: string;
    wrapClassName?: string;
    tooltipClassName?: string;
}

export default function EllipsisTooltip(props: EllipsisTooltipProps) {
    const {
        className,
        tailContent,
        tooltipClassName,
        tooltipContent,
        children,
        placement = 'top',
        trigger = TooltipTrigger.Hover,
        tooltipEnable = true,
        mouseEnterDelay = 800,
        mouseLeaveDelay = 0,
        wrapClassName,
        ...toolTipsProp
    } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const childrenWrapRef = useRef<HTMLDivElement>(null);
    const [isEllipsis, setIsEllipsis] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

    useEffect(() => {
        if (!tooltipEnable) {
            return;
        }

        function handleEllipsisVisible() {
            if (!containerRef.current || !childrenWrapRef.current) {
                setIsEllipsis(false);
                return;
            }
            const isEllipsis = containerRef.current.getBoundingClientRect().width
                < childrenWrapRef.current?.getBoundingClientRect().width;
            setIsEllipsis(isEllipsis);
        }
        let resizeObserver: ResizeObserver | null = null;

        if (containerRef.current && window.ResizeObserver) {
            resizeObserver = new window.ResizeObserver(throttle(handleEllipsisVisible, 100));
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver && resizeObserver.disconnect();
        };
    }, [tooltipEnable]);

    const contentNode = (
        <div ref={containerRef} className={classNames(styles.container, className)}>
            <span ref={childrenWrapRef}>{children}</span>
        </div>
    );

    const handleVisibleChange = useCallback((visible: boolean) => {
        if (!visible) {
            setTooltipVisible(false);
            return;
        }

        if (!containerRef.current || !childrenWrapRef.current) {
            setTooltipVisible(false);
            return;
        }
        const v = containerRef.current.getBoundingClientRect().width
            < childrenWrapRef.current?.getBoundingClientRect().width;
        setTooltipVisible(v);
    }, []);

    const tooltipModule = <Tooltip
        {...toolTipsProp}
        trigger={trigger}
        content={tooltipContent || children}
        placement={placement}
        mouseEnterDelay={mouseEnterDelay}
        mouseLeaveDelay={mouseLeaveDelay}
        visible={tooltipEnable && tooltipVisible}
        onChange={handleVisibleChange}
        className={classNames(styles.tooltip, tooltipClassName)}
    >
        {contentNode}
        {isEllipsis ?
            <span className={styles.tailContent}>{tailContent}</span>
            : null}
    </Tooltip>;

    return (
        tailContent ? <div className={classNames(styles.middleEllipsisWrap, wrapClassName)}>
            {tooltipModule}
        </div> :
            tooltipModule
    );
}
